/**
 * Supabase Client - Admin Console
 * WorkStream Admin - Gestão de Plataforma
 */
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://ijztnxsjxucnfrczgjcu.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlqenRueHNqeHVjbmZyY3pnamN1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM5NDQ0MTgsImV4cCI6MjA3OTUyMDQxOH0.yv-mwnqOX31ZZ7j81ljcWeYo_MNJtsTAtok8Xxhvc-Q'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ============================================================
// Admin Database Operations
// ============================================================

export const adminDb = {
  // Companies
  companies: {
    async getAll() {
      return supabase.from('companies').select('*').order('created_at', { ascending: false })
    },
    async getById(id: string) {
      return supabase.from('companies').select('*').eq('id', id).single()
    },
    async create(data: any) {
      return supabase.from('companies').insert(data).select().single()
    },
    async update(id: string, data: any) {
      return supabase.from('companies').update(data).eq('id', id).select().single()
    },
    async delete(id: string) {
      return supabase.from('companies').delete().eq('id', id)
    },
    async getStats() {
      const { data, error } = await supabase.from('companies').select('id, status, created_at')
      if (error) return { data: null, error }
      
      const total = data.length
      const active = data.filter((c: any) => c.status === 'active').length
      const thisMonth = data.filter((c: any) => {
        const created = new Date(c.created_at)
        const now = new Date()
        return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear()
      }).length
      
      return { data: { total, active, thisMonth }, error: null }
    }
  },

  // Users
  users: {
    async getAll() {
      return supabase.from('users').select('*').order('created_at', { ascending: false })
    },
    async getById(id: string) {
      return supabase.from('users').select('*').eq('id', id).single()
    },
    async create(data: any) {
      return supabase.from('users').insert(data).select().single()
    },
    async update(id: string, data: any) {
      return supabase.from('users').update(data).eq('id', id).select().single()
    },
    async delete(id: string) {
      return supabase.from('users').delete().eq('id', id)
    },
    async getStats() {
      const { data, error } = await supabase.from('users').select('id, role, status, created_at')
      if (error) return { data: null, error }
      
      const total = data.length
      const active = data.filter((u: any) => u.status === 'active').length
      const admins = data.filter((u: any) => u.role === 'admin').length
      
      return { data: { total, active, admins }, error: null }
    }
  },

  // Employees (all companies)
  employees: {
    async getAll() {
      return supabase.from('employees').select('*, companies(name)').order('created_at', { ascending: false })
    },
    async getByCompany(companyId: string) {
      return supabase.from('employees').select('*').eq('company_id', companyId).order('name')
    },
    async getStats() {
      const { data, error } = await supabase.from('employees').select('id, status, company_id')
      if (error) return { data: null, error }
      
      const total = data.length
      const active = data.filter((e: any) => e.status === 'active').length
      const companies = new Set(data.map((e: any) => e.company_id)).size
      
      return { data: { total, active, companies }, error: null }
    }
  },

  // Plans
  plans: {
    async getAll() {
      return supabase.from('plans').select('*').order('price', { ascending: true })
    },
    async getById(id: string) {
      return supabase.from('plans').select('*').eq('id', id).single()
    },
    async create(data: any) {
      return supabase.from('plans').insert(data).select().single()
    },
    async update(id: string, data: any) {
      return supabase.from('plans').update(data).eq('id', id).select().single()
    },
    async delete(id: string) {
      return supabase.from('plans').delete().eq('id', id)
    }
  },

  // Subscriptions
  subscriptions: {
    async getAll() {
      return supabase.from('subscriptions').select('*, companies(name), plans(name, price)').order('created_at', { ascending: false })
    },
    async getActive() {
      return supabase.from('subscriptions').select('*, companies(name), plans(name, price)').eq('status', 'active')
    },
    async getMRR() {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('plans(price)')
        .eq('status', 'active')
      
      if (error) return { data: null, error }
      
      const mrr = data.reduce((acc: number, sub: any) => acc + (sub.plans?.price || 0), 0)
      return { data: { mrr }, error: null }
    }
  },

  // Payments
  payments: {
    async getAll() {
      return supabase.from('payments').select('*, companies(name)').order('created_at', { ascending: false })
    },
    async getRecent(limit = 10) {
      return supabase.from('payments').select('*, companies(name)').order('created_at', { ascending: false }).limit(limit)
    },
    async getStats() {
      const { data, error } = await supabase.from('payments').select('amount, status, created_at')
      if (error) return { data: null, error }
      
      const total = data.reduce((acc: number, p: any) => acc + (p.amount || 0), 0)
      const successful = data.filter((p: any) => p.status === 'paid').length
      const pending = data.filter((p: any) => p.status === 'pending').length
      
      return { data: { total, successful, pending }, error: null }
    }
  },

  // Support Tickets
  tickets: {
    async getAll() {
      return supabase.from('support_tickets').select('*, companies(name), users(email)').order('created_at', { ascending: false })
    },
    async getOpen() {
      return supabase.from('support_tickets').select('*').in('status', ['open', 'in_progress'])
    },
    async getById(id: string) {
      return supabase.from('support_tickets').select('*, companies(name), users(email)').eq('id', id).single()
    },
    async update(id: string, data: any) {
      return supabase.from('support_tickets').update(data).eq('id', id).select().single()
    },
    async getStats() {
      const { data, error } = await supabase.from('support_tickets').select('id, status, priority')
      if (error) return { data: null, error }
      
      const total = data.length
      const open = data.filter((t: any) => t.status === 'open').length
      const critical = data.filter((t: any) => t.priority === 'critical').length
      
      return { data: { total, open, critical }, error: null }
    }
  },

  // ML Models
  models: {
    async getAll() {
      return supabase.from('model_versions').select('*').order('created_at', { ascending: false })
    },
    async getActive() {
      return supabase.from('model_versions').select('*').eq('status', 'active')
    },
    async getById(id: string) {
      return supabase.from('model_versions').select('*').eq('id', id).single()
    },
    async update(id: string, data: any) {
      return supabase.from('model_versions').update(data).eq('id', id).select().single()
    }
  },

  // Activity Logs
  activityLogs: {
    async getRecent(limit = 20) {
      return supabase.from('activity_logs').select('*').order('created_at', { ascending: false }).limit(limit)
    },
    async create(data: any) {
      return supabase.from('activity_logs').insert(data).select().single()
    }
  },

  // Alerts
  alerts: {
    async getAll() {
      return supabase.from('vision_alerts').select('*, employees(name), companies(name)').order('created_at', { ascending: false })
    },
    async getRecent(limit = 50) {
      return supabase.from('vision_alerts').select('*').order('created_at', { ascending: false }).limit(limit)
    },
    async getStats() {
      const { data, error } = await supabase.from('vision_alerts').select('id, tipo, severity, resolved')
      if (error) return { data: null, error }
      
      const total = data.length
      const critical = data.filter((a: any) => a.severity === 'critical').length
      const unresolved = data.filter((a: any) => !a.resolved).length
      
      return { data: { total, critical, unresolved }, error: null }
    }
  }
}

export default supabase

